import { useEffect, useLayoutEffect, useRef, useState } from "react";

import * as d3 from "d3";
import type { Feature, FeatureCollection, Geometry } from "geojson";

import { Svg_Zoom } from "../../components/Svg";
import { SetModalData, SetModalState } from "../../interface/PopUp";

// o countries.geojson traz o nome do país em properties.ADMIN
interface CountryProperties {
    ADMIN : string;
}

type CountryFeature = Feature<Geometry, CountryProperties>;
type CountryGeoData = FeatureCollection<Geometry, CountryProperties>;

export interface MapCountryData {
    country : string;
    users   : number;
}

interface MapTooltip {
    x     : number;
    y     : number;
    name  : string;
    users : number;
}

interface MapCanvasProps {
    data?   : MapCountryData[];
    geoData : CountryGeoData | null;
}

interface WorldMapProps {
    data? : MapCountryData[];
}

function buildMap(svgEl: SVGSVGElement, data: MapCountryData[], geoData: CountryGeoData){
    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();

    const width  = svgEl.clientWidth  || 800;
    const height = svgEl.clientHeight || 400;

    const userMap: { [key: string]: number } = {};
    data.forEach(d => { userMap[d.country] = d.users; });
    const maxUsers = Math.max(...data.map(d => d.users), 1);

    const projection = d3.geoNaturalEarth1()
        .scale(width / 6.2)
        .translate([width / 2, height / 2]);

    const path = d3.geoPath(projection);

    svg.selectAll<SVGPathElement, CountryFeature>("path")
        .data(geoData.features)
        .enter()
        .append("path")
        .attr("d", d => path(d))
        .attr("fill", d => {
            const users = userMap[d.properties.ADMIN] ?? 0;
            if (users > 0) {
                const t = 0.25 + (users / maxUsers) * 0.75;
                return d3.interpolate("#7a5010", "#EEA23F")(t);
            }
            return "#3a3a4a";
        })
        .attr("stroke", "#1a1a2a")
        .attr("stroke-width", 0.4);
}

export function MapCanvas({ data = [], geoData }: MapCanvasProps) {
    const ref = useRef<SVGSVGElement>(null);
    const [ tooltip, setTooltip ] = useState<MapTooltip | null>(null);

    useLayoutEffect(() => {
        if (!ref.current || !geoData) return;

        const svgEl = ref.current;

        const userMap: { [key: string]: number } = {};
        data.forEach(d => { userMap[d.country] = d.users; });

        buildMap(svgEl, data, geoData);

        d3.select(svgEl).selectAll<SVGPathElement, CountryFeature>("path")
            .on("mousemove", (event: MouseEvent, d) => {
                const users = userMap[d.properties.ADMIN];
                if (users) {
                    const rect = svgEl.getBoundingClientRect();
                    setTooltip({
                        x: event.clientX - rect.left,
                        y: event.clientY - rect.top,
                        name: d.properties.ADMIN,
                        users,
                    });
                }
            })
            .on("mouseleave", () => setTooltip(null));
    }, [data, geoData]);

    return (
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
            <svg ref={ ref } style={ { width: "100%", height: "100%", display: "block" } } />
            {
                tooltip && (
                    <div style={ {
                        position: "absolute",
                        left: tooltip.x + 10,
                        top: tooltip.y - 30,
                        background: "rgba(20,20,35,0.95)",
                        border: "1px solid rgba(238,162,63,0.4)",
                        color: "#fff",
                        fontSize: 12,
                        padding: "4px 8px",
                        borderRadius: 6,
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                        zIndex: 10,
                    } }>
                        <strong>{ tooltip.name }</strong> — { tooltip.users } users
                    </div>
                )
            }
        </div>
    );
}

export default function WorldMap({ data = [] }: WorldMapProps) {
    const svgRef = useRef<SVGSVGElement>(null);
    const [ geoData, setGeoData ] = useState<CountryGeoData | null>(null);
    const [ tooltip, setTooltip ] = useState<MapTooltip | null>(null);

    function drawMap(geo: CountryGeoData){
        if(!svgRef.current) return;

        const svgEl = svgRef.current;
        const svg   = d3.select(svgEl);
        svg.selectAll("*").remove();

        const width  = svgEl.clientWidth  || 400;
        const height = svgEl.clientHeight || 180;

        const userMap: { [key: string]: number } = {};
        data.forEach(d => { userMap[d.country] = d.users; });
        const maxUsers = Math.max(...data.map(d => d.users), 1);

        const projection = d3.geoNaturalEarth1()
            .scale(width / 6.2)
            .translate([width / 2, height / 2]);

        const path = d3.geoPath(projection);

        svg.selectAll<SVGPathElement, CountryFeature>("path")
            .data(geo.features)
            .enter()
            .append("path")
            .attr("d", d => path(d))
            .attr("fill", d => {
                const users = userMap[d.properties.ADMIN] ?? 0;
                if (users > 0) {
                    const t = 0.25 + (users / maxUsers) * 0.75;
                    return d3.interpolate("#7a5010", "#EEA23F")(t);
                }
                return "#3a3a4a";
            })
            .attr("stroke", "#1a1a2a")
            .attr("stroke-width", 0.4)
            .on("mousemove", (event: MouseEvent, d) => {
                const users = userMap[d.properties.ADMIN];
                if (users) {
                    const rect = svgEl.getBoundingClientRect();
                    setTooltip({
                        x: event.clientX - rect.left,
                        y: event.clientY - rect.top,
                        name: d.properties.ADMIN,
                        users,
                    });
                }
            })
            .on("mouseleave", () => setTooltip(null));
    }

    function OpenZoomMap(){
        if (!geoData) return;
        SetModalData('ZoomMap', { map: <MapCanvas data={ data } geoData={ geoData } /> });
        SetModalState('ZoomMap', true);
    }

    useEffect(() => {
        fetch("/countries.geojson")
            .then(r => r.json())
            .then((geo: CountryGeoData) => {
                setGeoData(geo);
                drawMap(geo);
            })
            .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
        <div style={{ position: "relative", width: "100%", height: "180px" }}>
            <div className="div_icon_zoom" onClick={ ()=>{ OpenZoomMap(); } }>
                <Svg_Zoom className="icon_zoom" color="#EEA23F" />
            </div>
            <svg ref={ svgRef } style={ { width: "100%", height: "100%", display: "block" } } />
            {
                tooltip && (
                    <div style={ {
                        position: "absolute",
                        left: tooltip.x + 10,
                        top: tooltip.y - 30,
                        background: "rgba(20,20,35,0.95)",
                        border: "1px solid rgba(238,162,63,0.4)",
                        color: "#fff",
                        fontSize: 12,
                        padding: "4px 8px",
                        borderRadius: 6,
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                    } }>
                        <strong>{ tooltip.name }</strong> — { tooltip.users } users
                    </div>
                )
            }
        </div>
    );
}
